import React, { Component } from 'react';
import { Box, Button, FormControl, Grid, Typography, Snackbar, Alert, SelectChangeEvent, Dialog, DialogContent, DialogContentText, Autocomplete, TextField } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { getbroker, } from '../ApiSevices/UserMasterApis';
import { getTemplateList, getBrokerTemplateConfig, saveBrokerTemplateConfig } from '../ApiSevices/ChecklistTempApi'
import { GridTable } from '../../Components/Sidebar/SidebarStyles';

interface ChecklistTemplateProps {
    displaysidebar: boolean;
}

interface Broker {
    Broker_ID: number;
    vchBrokerName: string;
}

interface Template {
    ID: number;
    VCH_CHECKLIST: string;
}

interface ChecklistTemplateState {
    broker: string;
    defaultTemplate: string;
    secondaryTemplate: string;
    brokers: Broker[];
    templates: Template[];
    snackbarOpen: boolean;
    openConfirmDialog: boolean;
    loading: boolean;
    snackbarMessage: string;
    snackbarSeverity: 'success' | 'error';
    tertiaryTemplate: string;
    quaternaryTemplate: string;
}

class ChecklistTemplate extends Component<ChecklistTemplateProps, ChecklistTemplateState> {
    private templateMap: Map<number, string>;
    private brokerMap: Map<string, Broker>;
    private _isMounted: boolean;

    constructor(props: ChecklistTemplateProps) {
        super(props);
        this.state = {
            broker: '',
            defaultTemplate: '',
            secondaryTemplate: '',
            brokers: [],
            templates: [],
            snackbarOpen: false,
            openConfirmDialog: false,
            loading: true,
            snackbarMessage: '',
            snackbarSeverity: 'success',
            tertiaryTemplate: '',
            quaternaryTemplate: '',
        };

        this.templateMap = new Map();
        this.brokerMap = new Map();
        this._isMounted = false;
    }

    async componentDidMount() {
        this._isMounted = true;
        this.setState({ loading: true });
        try {
            const [brokers, templates] = await Promise.all([
                getbroker(),
                getTemplateList()
            ]);

            if (this._isMounted) {
                templates?.sort((a: { VCH_CHECKLIST: string }, b: { VCH_CHECKLIST: string }) =>
                    a.VCH_CHECKLIST.localeCompare(b.VCH_CHECKLIST)
                );

                templates?.forEach((t: { ID: number; VCH_CHECKLIST: string }) => {
                    this.templateMap.set(t.ID, t.VCH_CHECKLIST);
                });

                brokers?.sort((a: Broker, b: Broker) =>
                    a.vchBrokerName.localeCompare(b.vchBrokerName)
                );

                brokers?.forEach((b: Broker) => {
                    this.brokerMap.set(b.vchBrokerName, b);
                });

                this.setState({
                    brokers,
                    templates,
                    loading: false
                });
            }
        } catch (err) {
            if (this._isMounted) {
                console.error('Error loading brokers/templates', err);
                this.setState({
                    loading: false,
                    snackbarMessage: 'Error loading data',
                    snackbarSeverity: 'error',
                    snackbarOpen: true
                });
            }
        } finally {
            this.setState({ loading: false });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    handleBrokerChange = async (event: any) => {
        const broker = event?.label ?? '';
        this.setState({
            broker,
            defaultTemplate: '',
            secondaryTemplate: '',
            tertiaryTemplate: '',
            quaternaryTemplate: '',
            loading: true
        });

        const selectedBroker = this.brokerMap.get(broker);
        if (!selectedBroker) {
            this.setState({ loading: false });
            return;
        }

        try {
            const config = await getBrokerTemplateConfig(selectedBroker.Broker_ID);
            if (!this._isMounted) return;

            this.setState({
                defaultTemplate: config.INT_TEMP_ID1 ? this.templateMap.get(config.INT_TEMP_ID1) || '' : '',
                secondaryTemplate: config.INT_TEMP_ID2 ? this.templateMap.get(config.INT_TEMP_ID2) || '' : '',
                tertiaryTemplate: config.INT_TEMP_ID3 ? this.templateMap.get(config.INT_TEMP_ID3) || '' : '',
                quaternaryTemplate: config.INT_TEMP_ID4 ? this.templateMap.get(config.INT_TEMP_ID4) || '' : '',
                loading: false
            });
        } catch (err) {
            if (this._isMounted) {
                console.error('Error fetching broker config', err);
                this.setState({
                    loading: false,
                    snackbarMessage: 'Error loading configuration',
                    snackbarSeverity: 'error',
                    snackbarOpen: true
                });
            }
        } finally {
            this.setState({ loading: false });
        }
    };

    handleTemplateChange = (newValue: any, key: string) => {
        this.setState({ [key]: newValue ? newValue.label : '' } as Pick<ChecklistTemplateState, keyof ChecklistTemplateState>);
    };

    handleSaveClick = () => {
        this.setState({ openConfirmDialog: true });
    };

    handleConfirmSave = async () => {
        const { broker, defaultTemplate, secondaryTemplate, tertiaryTemplate, quaternaryTemplate } = this.state;
        const brokerObj = this.brokerMap.get(broker);

        if (!brokerObj || !defaultTemplate) return;

        const templateValues = [defaultTemplate, secondaryTemplate, tertiaryTemplate, quaternaryTemplate].filter(Boolean);

        const uniqueValues = new Set(templateValues);
        if (uniqueValues.size !== templateValues.length) {
            this.setState({
                openConfirmDialog: false,
                snackbarMessage: 'All selected templates must be different!',
                snackbarSeverity: 'error',
                snackbarOpen: true,
                broker: '',
                defaultTemplate: '',
                secondaryTemplate: '',
                tertiaryTemplate: '',
                quaternaryTemplate: ''
            });
            return;
        }

        try {
            this.setState({ loading: true });

            const templateIds = [
                ...this.getTemplateId(defaultTemplate),
                ...this.getTemplateId(secondaryTemplate),
                ...this.getTemplateId(tertiaryTemplate),
                ...this.getTemplateId(quaternaryTemplate)
            ];

            await saveBrokerTemplateConfig({
                BrokerID: brokerObj.Broker_ID,
                INT_TEMP_ID1: templateIds[0] || 0,
                INT_TEMP_ID2: templateIds[1] || 0,
                INT_TEMP_ID3: templateIds[2] || 0,
                INT_TEMP_ID4: templateIds[3] || 0,
                NUM_AMT: 0
            });
            if (this._isMounted) {
                this.setState({
                    snackbarOpen: true,
                    loading: false,
                    openConfirmDialog: false,
                    broker: '',
                    defaultTemplate: '',
                    secondaryTemplate: '',
                    tertiaryTemplate: '',
                    quaternaryTemplate: '',
                    snackbarMessage: 'Checklist Template Saved Successfully!',
                    snackbarSeverity: 'success'
                });
            }
        } catch (err) {
            if (this._isMounted) {
                console.error(err);
                this.setState({
                    loading: false,
                    snackbarMessage: 'Failed to save configuration',
                    snackbarSeverity: 'error',
                    snackbarOpen: true
                });
            }
        }
    };

    private getTemplateId = (name: string): number[] => {
        if (!name) return [];
        const template = this.state.templates.find(t => t.VCH_CHECKLIST === name);
        return template ? [template.ID] : [];
    };

    handleCloseConfirm = () => {
        this.setState({
            openConfirmDialog: false,
            broker: '',
            defaultTemplate: '',
            secondaryTemplate: '',
            tertiaryTemplate: '',
            quaternaryTemplate: ''
        });
    };

    handleCloseSnackbar = () => {
        this.setState({ snackbarOpen: false });
    };

    get isSaveDisabled() {
        const { broker, defaultTemplate } = this.state;
        return !broker || !defaultTemplate;
    }

    render() {
        const { broker, defaultTemplate, secondaryTemplate, brokers, snackbarOpen, snackbarMessage, snackbarSeverity, openConfirmDialog, loading, templates, tertiaryTemplate, quaternaryTemplate } = this.state;
        const { displaysidebar } = this.props;

        const templateFields = [
            {
                key: 'defaultTemplate' as const,
                label: 'Select Default Template',
                value: defaultTemplate,
                required: true
            },
            {
                key: 'secondaryTemplate' as const,
                label: 'Select Secondary Template',
                value: secondaryTemplate,
                required: false
            },
            {
                key: 'tertiaryTemplate' as const,
                label: 'Select Tertiary Template',
                value: tertiaryTemplate,
                required: false
            },
            {
                key: 'quaternaryTemplate' as const,
                label: 'Select Quaternary Template',
                value: quaternaryTemplate,
                required: false
            }
        ];

        return (
            <GridTable displaysidebar={displaysidebar}>
                <Box sx={{
                    padding: '2px 0',
                    backgroundColor: '#f8f8f8',
                    borderBottom: '1px solid #ddd',
                    marginBottom: '25px'
                }}>
                    <Typography variant="subtitle2" sx={{
                        fontWeight: 'bold',
                        color: '#000',
                        ml: 1,
                        textTransform: 'uppercase'
                    }}>
                        CHECKLIST TEMPLATE
                    </Typography>
                </Box>

                <Box sx={{
                    p: 2,
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '700px', md: '900px' },
                    backgroundColor: '#fff',
                    border: '2px solid #ddd'
                }}>
                    {/* Broker Selection */}
                    <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Grid item xs={12} sm={4} md={3}>
                            <Typography sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#000',
                                whiteSpace: 'nowrap'
                            }}>
                                Select Broker <Box component="span" sx={{ color: 'red' }}>*</Box>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={8} md={9}>
                            <FormControl fullWidth size="small">
                                <Autocomplete
                                    options={brokers.map((b) => ({ label: b.vchBrokerName, id: b.Broker_ID }))}
                                    size="small"
                                    getOptionLabel={(option) => option?.label || ''}
                                    value={brokers.filter((e: any) => e.vchBrokerName === broker).map((e: any) => ({ label: e.vchBrokerName, id: e.Broker_ID }))[0] || null}
                                    onChange={(_event, newValue) => this.handleBrokerChange(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder="Select Broker..."
                                            size="medium"
                                            InputProps={{
                                                ...params.InputProps,
                                                style: { height: 25, fontSize: "11px" }
                                            }}
                                        />
                                    )}
                                    fullWidth
                                    slotProps={{ listbox: { sx: { fontSize: '11px' } } }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Template Selections */}
                    {templateFields.map(({ key, label, value, required }) => (
                        <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }} key={key}>
                            <Grid item xs={12} sm={4} md={3}>
                                <Typography sx={{
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    color: '#000',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {label} {required && <Box component="span" sx={{ color: 'red' }}>*</Box>}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={8} md={9}>
                                <FormControl fullWidth size="small">
                                    <Autocomplete
                                        options={templates.map((b) => ({ label: b.VCH_CHECKLIST, id: b.VCH_CHECKLIST }))}
                                        size="small"
                                        getOptionLabel={(option) => option?.label || ''}
                                        value={templates.filter((e: any) => e.VCH_CHECKLIST === value).map((e: any) => ({ label: e.VCH_CHECKLIST, id: e.VCH_CHECKLIST }))[0] || null}
                                        onChange={(_event, newValue) => this.handleTemplateChange(newValue, key)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                placeholder="Select Broker..."
                                                size="medium"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    style: { height: 25, fontSize: "11px" }
                                                }}
                                            />
                                        )}
                                        fullWidth
                                        slotProps={{ listbox: { sx: { fontSize: '11px' } } }}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    ))}

                    <Box sx={{ textAlign: 'right', mt: 2 }}>
                        <Button
                            variant="contained"
                            disabled={this.isSaveDisabled || loading}
                            onClick={this.handleSaveClick}
                            sx={{
                                backgroundColor: '#1e1466',
                                fontSize: '11px',
                                padding: '2px 12px',
                                minWidth: 60,
                                height: 28,
                                '&:disabled': {
                                    backgroundColor: '#e0e0e0',
                                    color: '#a0a0a0'
                                }
                            }}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>

                {/* Confirmation Dialog */}
                <Dialog open={openConfirmDialog} onClose={this.handleCloseConfirm}>
                    <DialogContent sx={{
                        padding: '0px 20px',
                        paddingTop: '25px',
                        paddingBottom: '35px',
                        textAlign: 'center',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 2,
                        }}>
                            <Box sx={{
                                backgroundColor: '#ECFDF5',
                                borderRadius: '50%',
                                width: 60,
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <SaveIcon sx={{ color: '#22c55e', fontSize: 30 }} />
                            </Box>
                        </Box>

                        <DialogContentText sx={{
                            textAlign: 'center',
                            mt: 1,
                            mb: 3,
                            fontWeight: 'bold',
                            color: 'rgba(0, 0, 0, 0.7)',
                            fontSize: '14px',
                        }}>
                            Are you sure you want to save the changes?
                        </DialogContentText>

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, height: '30px' }}>
                            <Button
                                onClick={this.handleConfirmSave}
                                variant="contained"
                                sx={{
                                    px: 4,
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    backgroundColor: '#1e1466',
                                    '&:hover': {
                                        backgroundColor: '#0f0c44',
                                    },
                                }}
                                disabled={loading}
                            >
                                YES
                            </Button>

                            <Button
                                onClick={this.handleCloseConfirm}
                                sx={{
                                    color: '#007bff',
                                    border: '0.5px solid #53494987',
                                    px: 4,
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    '&:hover': {
                                        borderColor: '#0056b3',
                                        color: '#0056b3',
                                    },
                                }}
                            >
                                NO
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={this.handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={this.handleCloseSnackbar}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>

                {loading && (
                    <Box sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        zIndex: 9999
                    }}>
                        <div className="loader"></div>
                    </Box>
                )}
            </GridTable>
        );
    }
}

export default ChecklistTemplate;